// Admin portal TypeScript types

export type LeadStatus = 'New' | 'Call Done' | 'Not Responding' | 'Not Eligible' | 'Spam' | 'Added in CRM' | 'Already Existing' | 'Rejected' | 'Deleted';

export interface UnifiedLead {
  id: number;
  name: string;
  phone: string;
  email: string;
  country?: string;
  country_code?: string;
  message?: string;
  lead_type: 'contact' | 'eligibility';
  source_page: string | null;
  source_form: string | null;
  form_position?: string | null;
  button_name?: string | null;
  cta_name?: string | null;
  cta_page?: string | null;
  score: number | null;
  visa_recommendation: string | null;
  status: LeadStatus;
  remark: string | null;
  uid: string | null;
  called_date: string | null;
  assigned_to: string | null;
  follow_up_date: string | null;
  created_at: string;
  updated_at: string;
  delivery_channel?: 'web' | 'beacon' | 'retry' | string;
  spam_flags?: string;
}

export interface LeadNote {
  id: number;
  lead_id: number;
  note: string;
  created_by: string | null;
  created_at: string;
}

export interface AdminUser {
  id: number;
  email: string;
  name: string | null;
  role: string;
}

export interface DashboardStats {
  totalLeads: number;
  newToday: number;
  callDone: number;
  notResponding: number;
  addedInCRM: number;
  bySource: Record<string, number>;
  byStatus: Record<string, number>;
}

export interface LeadFilters {
  search: string;
  status: LeadStatus | 'All';
  sourcePage: string;
  dateFrom: string;
  dateTo: string;
}

export interface LeadsResponse {
  leads: UnifiedLead[];
  total: number;
  page: number;
  totalPages: number;
}

export const LEAD_STATUSES: LeadStatus[] = ['New', 'Call Done', 'Not Responding', 'Not Eligible', 'Spam', 'Added in CRM', 'Already Existing', 'Rejected', 'Deleted'];

/** Statuses shown in the main leads table by default */
export const PRIMARY_STATUSES: LeadStatus[] = [
  'New',
  'Call Done',
  'Not Responding',
  'Added in CRM',
  'Already Existing',
];

/** Statuses kept in the separate Flagged & Other section */
export const FLAGGED_STATUSES: LeadStatus[] = [
  'Spam',
  'Not Eligible',
  'Rejected',
  'Deleted',
];

export function isFlaggedLead(lead: Pick<UnifiedLead, 'status' | 'spam_flags'>): boolean {
  return FLAGGED_STATUSES.includes(lead.status) || !!lead.spam_flags;
}

export const STATUS_COLORS: Record<string, { bg: string; text: string; dot: string }> = {
  'New': { bg: 'bg-blue-50', text: 'text-blue-700', dot: 'bg-blue-500' },
  'Call Done': { bg: 'bg-green-50', text: 'text-green-700', dot: 'bg-green-500' },
  'Not Responding': { bg: 'bg-orange-50', text: 'text-orange-700', dot: 'bg-orange-500' },
  'Not Eligible': { bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500' },
  'Spam': { bg: 'bg-gray-50', text: 'text-gray-700', dot: 'bg-gray-400' },
  'Added in CRM': { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  'Already Existing': { bg: 'bg-purple-50', text: 'text-purple-700', dot: 'bg-purple-500' },
  'Rejected': { bg: 'bg-rose-50', text: 'text-rose-700', dot: 'bg-rose-500' },
  'Deleted': { bg: 'bg-zinc-50', text: 'text-zinc-700', dot: 'bg-zinc-500' },
};

export const DEFAULT_STATUS_COLOR = { bg: 'bg-slate-50', text: 'text-slate-700', dot: 'bg-slate-400' };
